import { useEffect, useMemo, useRef, useState } from "react";
import { PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  MAX_BLOCK_SET_COUNT,
  useOptionalManualProgram,
} from "../context/ManualProgramContext";
import { useEditableProgram } from "../context/EditableProgramContext";
import { useCycleEditorRestore } from "../context/useCycleEditorRestore";
import { useWeeklyPlanRestore } from "../context/useWeeklyPlanRestore";
import {
  getOrderIndexFallbackWeekdayIndex,
  getDateKeyInTimeZone,
  resolveOccurrenceTemporalState,
} from "../features/multiWeek/occurrence";
import { getCycleWorkoutEditorPath } from "../features/multiWeek/routes";
import { resolveBackTarget } from "../features/weeklyPlans/navigation";
import WorkoutBuilderRendererV2 from "../features/workoutBuilder/WorkoutBuilderRendererV2";
import { getBlockUiKey, isSortableBlockType } from "../features/workoutBuilder/sortableBlock";
import {
  DEFAULT_SEARCH_LIMIT,
  DIFFICULTY_OPTIONS,
  EQUIPMENT_OPTIONS,
  FILTER_ONLY_SEARCH_LIMIT,
  FOCUS_OPTIONS_BY_MUSCLE,
  MUSCLE_OPTIONS,
  TYPE_OPTIONS,
  getExerciseTrainingType,
  hasExcludedExerciseMatch,
  rankExercises,
} from "../features/workoutBuilder/searchFilters";
import {
  MAX_REP_TARGET,
  MAX_TARGET_SECONDS,
  REP_MODES,
  clampRepTargetValue,
  resolveSetRepPrescription,
  resolveStrengthRepFields,
} from "../features/workoutBuilder/repPrescription";
import { fetchUserExercisePoolResponse } from "../services/api";
import { resolveCardioModality } from "../utils/cardioModality";
import { computeWorkoutMetrics } from "../utils/workoutMetrics";
import { usePlannedMuscleVolume } from "../features/muscleVolume/usePlannedMuscleVolume";

function normalizeTempoValue(value) {
  const digits = String(value || "")
    .replace(/\D/g, "")
    .split("")
    .filter((digit) => digit >= "0" && digit <= "5")
    .join("")
    .slice(0, 4);

  if (!digits) {
    return "";
  }

  if (digits.length === 3) {
    return `${digits}0`;
  }

  return digits;
}

function formatTempoValue(value) {
  const normalized = normalizeTempoValue(value);

  if (!normalized) {
    return "";
  }

  return normalized.split("").join("-");
}

const REST_OPTIONS = ["30s", "45s", "60s", "75s", "90s", "120s", "150s", "180s", "240s", "300s"];
const RIR_CYCLE = [2, 1, 0, 4, 3];

function getNextRestValue(currentValue, direction) {
  const currentIndex = Math.max(0, REST_OPTIONS.indexOf(currentValue));
  const nextIndex = Math.min(
    REST_OPTIONS.length - 1,
    Math.max(0, currentIndex + direction)
  );

  return REST_OPTIONS[nextIndex];
}

function getNextRirValue(currentValue) {
  const currentIndex = RIR_CYCLE.indexOf(Number(currentValue));
  const safeIndex = currentIndex === -1 ? 0 : currentIndex;
  return RIR_CYCLE[(safeIndex + 1) % RIR_CYCLE.length];
}

const CARDIO_HEART_RATE_MODE_OPTIONS = [
  { value: "none", label: "None" },
  { value: "avg_bpm", label: "BPM" },
  { value: "zone", label: "Zone" },
];

const CARDIO_MACHINE_SETTINGS_BY_MODALITY = {
  treadmill_walk: [
    { value: "speed", label: "Speed" },
    { value: "incline", label: "Incline" },
  ],
  incline_treadmill_walk: [
    { value: "speed", label: "Speed" },
    { value: "incline", label: "Incline" },
  ],
  stationary_bike: [{ value: "resistance", label: "Resistance" }],
  recumbent_bike: [{ value: "resistance", label: "Resistance" }],
  stair_climber: [{ value: "level", label: "Level" }],
  elliptical: [{ value: "resistance", label: "Resistance" }],
  rowing_machine: [{ value: "pace", label: "Pace" }],
};

// The add-block sheet's choices and their consequences are preserved exactly as
// they exist today: Single and Superset are explained but not creatable from
// here, and no new training concept is introduced (blueprint rule G3).
const ADD_BLOCK_OPTIONS = [
  {
    value: "single",
    label: "Single",
    help: "Add from search only",
    icon: "exercise",
    disabled: true,
  },
  {
    value: "superset",
    label: "Superset",
    help: "Convert an existing single block",
    icon: "layers",
    disabled: true,
  },
  { value: "circuit", label: "Circuit", help: "Rapid fire routine", icon: "bolt" },
  { value: "emom", label: "EMOM", help: "Every min on the min", icon: "timer" },
];

// The builder's exercise search is served by /api/exercise-pool, whose items
// (exercisePoolBuilder.createItem) carry their taxonomy under `attributes`.
// The builder's canonical single-block / superset-lane shape keeps `bodyParts`
// and `muscleFocus` at the top level -- that is what the draft persists and
// what computeWorkoutMetrics reads for muscle distribution. Normalize once at
// this boundary so a picked exercise enters the draft with its taxonomy
// intact; a result that already exposes the flat shape is passed through.
function toBuilderExerciseSelection(exercise) {
  const attributes = exercise?.attributes || {};
  const pick = (field) => {
    if (Array.isArray(exercise?.[field])) {
      return exercise[field];
    }

    return Array.isArray(attributes[field]) ? attributes[field] : [];
  };

  return {
    ...exercise,
    bodyParts: pick("bodyParts"),
    muscleFocus: pick("muscleFocus"),
  };
}

function getCardioModality(block) {
  return String(
    block?.exercise?.cardioModality ||
      block?.cardioModality ||
      resolveCardioModality(block?.exerciseId || block?.exercise?.exerciseId) ||
      ""
  )
    .trim()
    .toLowerCase();
}

function getCardioMachineSettingOptions(block, currentKey = "") {
  const modalityOptions =
    CARDIO_MACHINE_SETTINGS_BY_MODALITY[getCardioModality(block)] || [];
  const normalizedCurrentKey = String(currentKey || "").trim().toLowerCase();

  if (
    modalityOptions.length === 0 &&
    normalizedCurrentKey &&
    !modalityOptions.some((option) => option.value === normalizedCurrentKey)
  ) {
    return [
      ...modalityOptions,
      { value: normalizedCurrentKey, label: normalizedCurrentKey },
    ];
  }

  return modalityOptions;
}

function getCardioMachineSettingLabel(block, key = "") {
  return (
    getCardioMachineSettingOptions(block, key).find((option) => option.value === key)
      ?.label || key
  );
}

function getUsedCardioMachineSettingKeys(machineSettings = []) {
  return new Set(
    machineSettings
      .map((setting) => String(setting?.key || "").trim().toLowerCase())
      .filter(Boolean)
  );
}

function getAvailableCardioMachineSettingOptions(block, machineSettings = []) {
  const usedKeys = getUsedCardioMachineSettingKeys(machineSettings);

  return getCardioMachineSettingOptions(block).filter(
    (option) => !usedKeys.has(option.value)
  );
}

function getCardioBlockExerciseName(block) {
  if (block?.exercise && typeof block.exercise === "object") {
    return block.exercise.name || "Cardio";
  }

  return block?.exercise || "Cardio";
}

function getCardioPrescription(block) {
  const prescription = block?.cardioPrescription || {};

  return {
    durationMinutes: prescription.durationMinutes ?? "",
    heartRateTargetMode: prescription.heartRateTargetMode,
    heartRateTargetValue: prescription.heartRateTargetValue ?? "",
    machineSettings: Array.isArray(prescription.machineSettings)
      ? prescription.machineSettings.slice(0, 2).map((setting) => ({
          key: String(setting?.key || "").trim().toLowerCase(),
          value: String(setting?.value ?? ""),
        }))
      : [],
    notes: prescription.notes ?? "",
  };
}

function clampCardioNumber(value, min, max, fallback) {
  const parsed = Number(value);
  const safeValue = Number.isFinite(parsed) ? parsed : fallback;

  return Math.min(max, Math.max(min, safeValue));
}

function formatMinutes(value) {
  return `${value}m`;
}

function getMetricBarWidth(value, maxValue) {
  if (value <= 0 || maxValue <= 0) {
    return "0%";
  }

  return `${Math.max(8, Math.min(100, (value / maxValue) * 100))}%`;
}

function resolveMultiWeekBuilderPath(pathname) {
  const builderMatch = pathname.match(/^(\/program\/cycles\/[^/]+\/builder)/);
  if (builderMatch) {
    return builderMatch[1];
  }

  const workoutMatch = pathname.match(
    /^(\/program\/cycles\/[^/]+\/builder)(?:\/week\/[^/]+\/workout\/[^/]+|\/workout\/[^/]+)$/
  );
  if (workoutMatch) {
    return workoutMatch[1];
  }

  return "/program/manual-builder-multi";
}

// The two implicit strength slots: left ("reps") and right ("maxReps").
const REP_DRAFT_FIELDS = ["reps", "maxReps", "targetSeconds"];

export default function ManualWorkoutEditor() {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showAddBlockSheet, setShowAddBlockSheet] = useState(false);
  const [openBlockId, setOpenBlockId] = useState(null);
  const [openSupersetExerciseByBlock, setOpenSupersetExerciseByBlock] = useState({});
  const [isWorkoutTitleEditing, setIsWorkoutTitleEditing] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [openFilterMenu, setOpenFilterMenu] = useState(null);
  const [pendingScrollToLastBlock, setPendingScrollToLastBlock] = useState(false);
  const [showMuscleDistribution, setShowMuscleDistribution] = useState(false);
  const [tempoDrafts, setTempoDrafts] = useState({});
  const [repsDrafts, setRepsDrafts] = useState({});
  const [searchFilters, setSearchFilters] = useState({
    muscle: [],
    focus: [],
    equipment: [],
    type: [],
    difficulty: [],
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [exerciseResults, setExerciseResults] = useState([]);
  const [isLoadingExercises, setIsLoadingExercises] = useState(false);
  const [exerciseError, setExerciseError] = useState("");
  const [exerciseEmptyMessage, setExerciseEmptyMessage] = useState("No exercises found.");
  const [activeSearchTarget, setActiveSearchTarget] = useState(null);
  const [activeSortableId, setActiveSortableId] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { cycleId, workoutId, weekNumber, orderIndex } = useParams();
  const { isRestoring: isRestoringCycle, isUnavailable: isCycleUnavailable } =
    useCycleEditorRestore(cycleId);
  const cycleUnavailableLeftRef = useRef(null);
  const {
    isRestoring: isRestoringWeeklyPlan,
    isUnavailable: isWeeklyPlanUnavailable,
    openedByRestore: weeklyOpenedByRestore,
  } = useWeeklyPlanRestore(useOptionalManualProgram(), { enabled: !cycleId });
  const searchUiRef = useRef(null);
  const workoutTitleInputRef = useRef(null);
  const {
    programDraft,
    draftMetadata,
    getMultiWeekTodayDateKey,
    setSelectedWeek,
    updateWorkoutName,
    updateBlock,
    reorderBlocks,
    updateSupersetExercise,
    updateSupersetSetCount,
    removeBlock,
    addSet,
    removeSet,
    updateSet,
    appendSingleBlockFromExercise,
    convertSingleBlockToSuperset,
    assignSupersetExercise,
    clearSupersetLaneExercise,
    duplicateBlock,
    hasIncompleteSupersets,
  } = useEditableProgram();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 180, tolerance: 8 },
    })
  );
  const parsedWeekNumber = Number(weekNumber);
  const parsedOrderIndex = Number(orderIndex);
  const hasCanonicalMultiWeekRoute =
    Number.isInteger(parsedWeekNumber) &&
    parsedWeekNumber > 0 &&
    Number.isInteger(parsedOrderIndex) &&
    parsedOrderIndex > 0;

  const multiWeekWorkoutMatch = useMemo(() => {
    if (!programDraft.isMultiWeek) {
      return null;
    }

    if (hasCanonicalMultiWeekRoute) {
      const matchedWeek = (programDraft.weeks || []).find(
        (week) => week.weekNumber === parsedWeekNumber
      );
      if (!matchedWeek) {
        return null;
      }

      const matchedWorkout = (matchedWeek.workouts || []).find(
        (item) => item.orderIndex === parsedOrderIndex
      );

      if (matchedWorkout) {
        return {
          workout: matchedWorkout,
          weekNumber: matchedWeek.weekNumber,
        };
      }

      return null;
    }

    for (const week of programDraft.weeks || []) {
      const matchedWorkout = (week.workouts || []).find((item) => item.id === workoutId);

      if (matchedWorkout) {
        return {
          workout: matchedWorkout,
          weekNumber: week.weekNumber,
        };
      }
    }

    return null;
  }, [
    hasCanonicalMultiWeekRoute,
    parsedOrderIndex,
    parsedWeekNumber,
    programDraft.isMultiWeek,
    programDraft.weeks,
    workoutId,
  ]);

  const workout = useMemo(() => {
    if (programDraft.isMultiWeek) {
      return multiWeekWorkoutMatch?.workout || null;
    }

    if (!programDraft.workouts.length) {
      return null;
    }

    const matched = programDraft.workouts.find((item) => item.id === workoutId);
    if (matched) {
      return matched;
    }
    // A restored (reload / deep link) URL names a workout of the stored plan:
    // an unknown id is invalid, not "the first workout".
    return weeklyOpenedByRestore ? null : programDraft.workouts[0];
  }, [
    multiWeekWorkoutMatch,
    programDraft.isMultiWeek,
    programDraft.workouts,
    weeklyOpenedByRestore,
    workoutId,
  ]);
  const sortableBlockIds = useMemo(() => {
    if (!workout) {
      return [];
    }

    return (workout.blocks || [])
      .map((block, blockIndex) => ({ block, blockIndex }))
      .filter(({ block }) => isSortableBlockType(block.type))
      .map(({ block, blockIndex }) => getBlockUiKey(workout.id, block, blockIndex));
  }, [workout]);

  useEffect(() => {
    if (!programDraft.isMultiWeek || hasCanonicalMultiWeekRoute || !multiWeekWorkoutMatch?.workout) {
      return;
    }

    navigate(
      getCycleWorkoutEditorPath(
        cycleId,
        multiWeekWorkoutMatch.weekNumber,
        multiWeekWorkoutMatch.workout.orderIndex
      ),
      {
      replace: true,
      state: location.state,
      }
    );
  }, [
    cycleId,
    hasCanonicalMultiWeekRoute,
    location.state,
    multiWeekWorkoutMatch,
    navigate,
    programDraft.isMultiWeek,
  ]);

  useEffect(() => {
    if (!programDraft.isMultiWeek || !multiWeekWorkoutMatch?.weekNumber) {
      return;
    }

    if (programDraft.selectedWeek !== multiWeekWorkoutMatch.weekNumber) {
      setSelectedWeek(multiWeekWorkoutMatch.weekNumber);
    }
  }, [
    multiWeekWorkoutMatch,
    programDraft.isMultiWeek,
    programDraft.selectedWeek,
    setSelectedWeek,
  ]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isCycleUnavailable && cycleUnavailableLeftRef.current !== cycleId) {
      // No usable draft to edit: the read-only cycle view, never a new draft.
      // Once per cycle: `navigate` changes identity with every location change.
      cycleUnavailableLeftRef.current = cycleId;
      navigate(`/program/cycles/${cycleId}`, { replace: true });
    }
  }, [cycleId, isCycleUnavailable, navigate]);

  useEffect(() => {
    // The cycle in the URL is still being restored: no redirect decision yet.
    if (isRestoringCycle || isCycleUnavailable || isRestoringWeeklyPlan || isWeeklyPlanUnavailable) {
      return;
    }

    const multiWeekBuilderPath = resolveMultiWeekBuilderPath(location.pathname);

    if (
      programDraft.isMultiWeek &&
      (!draftMetadata.loadedFromBackend || (!programDraft.weeks || programDraft.weeks.length === 0))
    ) {
      return;
    }

    if (!workout && programDraft.isMultiWeek) {
      const validRequestedWeek =
        hasCanonicalMultiWeekRoute &&
        (programDraft.weeks || []).some((week) => week.weekNumber === parsedWeekNumber);

      if (validRequestedWeek && programDraft.selectedWeek !== parsedWeekNumber) {
        setSelectedWeek(parsedWeekNumber);
      }

      navigate(multiWeekBuilderPath, { replace: true });
      return;
    }

    if (!workout) {
      navigate("/program/manual-builder", { replace: true });
    }
  }, [
    draftMetadata.loadedFromBackend,
    hasCanonicalMultiWeekRoute,
    isCycleUnavailable,
    isRestoringCycle,
    isRestoringWeeklyPlan,
    isWeeklyPlanUnavailable,
    location.pathname,
    navigate,
    parsedWeekNumber,
    programDraft.isMultiWeek,
    programDraft.selectedWeek,
    programDraft.weeks,
    setSelectedWeek,
    workout,
  ]);

  const hasIncompleteSuperset = hasIncompleteSupersets(workout?.id ?? null);
  const workoutOccurrenceState = useMemo(
    () =>
      resolveOccurrenceTemporalState({
        cycleStartDate: programDraft.startDate,
        weekNumber: multiWeekWorkoutMatch?.weekNumber || parsedWeekNumber || 1,
        scheduledDay: workout?.scheduledDay || null,
        weekdayIndex:
          getOrderIndexFallbackWeekdayIndex(workout?.orderIndex) ??
          getOrderIndexFallbackWeekdayIndex(parsedOrderIndex),
              todayDateKey: getMultiWeekTodayDateKey
              ? getMultiWeekTodayDateKey()
              : draftMetadata.draftState?.localDate ||
                getDateKeyInTimeZone(
                  draftMetadata.timezone || programDraft.timezone || "America/Toronto"
                ),
      }),
      [
        draftMetadata.draftState?.localDate,
        draftMetadata.timezone,
        getMultiWeekTodayDateKey,
        multiWeekWorkoutMatch?.weekNumber,
        parsedOrderIndex,
        parsedWeekNumber,
        programDraft.startDate,
        programDraft.timezone,
        workout?.orderIndex,
        workout?.scheduledDay,
      ]
  );
  const isPastWorkoutOccurrenceLocked =
    Boolean(workout?.executionLock?.executionId) ||
    (programDraft.isMultiWeek &&
      draftMetadata.temporalStatus === "active" &&
      workoutOccurrenceState.isPastOccurrence);
  const shouldShowSearchPanel =
    !isPastWorkoutOccurrenceLocked &&
    (isSearchOpen || Boolean(debouncedSearchQuery) || Boolean(activeSearchTarget));
  const hasStructuredFilters = useMemo(
    () =>
      searchFilters.muscle.length > 0 ||
      searchFilters.focus.length > 0 ||
      searchFilters.equipment.length > 0 ||
      searchFilters.type.length > 0 ||
      searchFilters.difficulty.length > 0,
    [searchFilters]
  );
  const availableFocusOptions = useMemo(() => {
    const optionsByValue = new Map();

    searchFilters.muscle.forEach((muscleValue) => {
      (FOCUS_OPTIONS_BY_MUSCLE[muscleValue] || []).forEach((option) => {
        optionsByValue.set(option.value, option);
      });
    });

    return Array.from(optionsByValue.values());
  }, [searchFilters.muscle]);
  const visibleFilters = useMemo(() => {
    const filters = [{ key: "muscle", label: "Muscle", options: MUSCLE_OPTIONS }];

    if (availableFocusOptions.length > 0) {
      filters.push({ key: "focus", label: "Focus", options: availableFocusOptions });
    }

    filters.push(
      { key: "equipment", label: "Equipment", options: EQUIPMENT_OPTIONS },
      { key: "type", label: "Type", options: TYPE_OPTIONS },
      { key: "difficulty", label: "Difficulty", options: DIFFICULTY_OPTIONS }
    );

    return filters;
  }, [availableFocusOptions]);
  const searchLimit = useMemo(() => {
    if (debouncedSearchQuery) {
      return DEFAULT_SEARCH_LIMIT;
    }

    if (hasStructuredFilters) {
      return FILTER_ONLY_SEARCH_LIMIT;
    }

    return DEFAULT_SEARCH_LIMIT;
  }, [debouncedSearchQuery, hasStructuredFilters]);
  const structuredQueryFilters = useMemo(
    () => ({
      bodyParts: searchFilters.muscle,
      muscleFocus: searchFilters.focus,
      equipmentCategory: searchFilters.equipment,
      trainingType: searchFilters.type,
      difficulty: searchFilters.difficulty,
    }),
    [searchFilters]
  );
  const rankedExerciseResults = useMemo(
    () => rankExercises(exerciseResults, debouncedSearchQuery),
    [exerciseResults, debouncedSearchQuery]
  );
  const visibleExerciseResults = useMemo(
    () =>
      activeSearchTarget
        ? rankedExerciseResults.filter(
            (exercise) => getExerciseTrainingType(exercise) !== "cardio"
          )
        : rankedExerciseResults,
    [activeSearchTarget, rankedExerciseResults]
  );
  const workoutMetrics = useMemo(() => computeWorkoutMetrics(workout), [workout]);
  const volumeWorkouts = useMemo(() => (workout ? [workout] : []), [workout]);
  const muscleVolume = usePlannedMuscleVolume(volumeWorkouts);
  const visibleMuscleDistribution = useMemo(
    () => workoutMetrics.muscleDistribution.filter((item) => item.rawSets > 0),
    [workoutMetrics.muscleDistribution]
  );
  const workoutStatItems = useMemo(
    () => [
      {
        label: "Exercises",
        value: String(workoutMetrics.exerciseCount),
        width: getMetricBarWidth(workoutMetrics.exerciseCount, 12),
      },
      {
        label: "Sets",
        value: String(workoutMetrics.setCount),
        width: getMetricBarWidth(workoutMetrics.setCount, 32),
      },
      {
        label: "Dur.",
        value: formatMinutes(workoutMetrics.estimatedDurationMinutes),
        width: getMetricBarWidth(workoutMetrics.estimatedDurationMinutes, 120),
      },
      {
        label: "TUT",
        value: formatMinutes(workoutMetrics.totalTUTMinutes),
        width: getMetricBarWidth(workoutMetrics.totalTUTMinutes, 60),
      },
    ],
    [workoutMetrics]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 250);

    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    let cancelled = false;

    async function loadExercises() {
      setIsLoadingExercises(true);
      setExerciseError("");
      setExerciseEmptyMessage("No exercises found.");

      try {
        // Bounded retrieval for this pass; structured filtering is real but results are still not exhaustive.
        const poolParams = {
          q: debouncedSearchQuery,
          limit: searchLimit,
          ...structuredQueryFilters,
        };
        const response = await fetchUserExercisePoolResponse(poolParams);
        let results = Array.isArray(response.items) ? response.items : [];
        let emptyMessage = "No exercises found.";

        if (results.length === 0 && debouncedSearchQuery.trim()) {
          const debugResponse = await fetchUserExercisePoolResponse({
            ...poolParams,
            includeExcluded: true,
          });

          results = Array.isArray(debugResponse.items) ? debugResponse.items : [];

          if (results.length === 0 && hasExcludedExerciseMatch(debugResponse, debouncedSearchQuery)) {
            emptyMessage = "No exercises available with your current profile filters.";
          }
        }

        if (!cancelled) {
          setExerciseResults(results);
          setExerciseEmptyMessage(emptyMessage);
        }
      } catch (error) {
        if (!cancelled) {
          setExerciseResults([]);
          setExerciseError(error.message || "Unable to load exercises.");
        }
      } finally {
        if (!cancelled) {
          setIsLoadingExercises(false);
        }
      }
    }

    loadExercises();

    return () => {
      cancelled = true;
    };
  }, [debouncedSearchQuery, searchLimit, structuredQueryFilters]);

  useEffect(() => {
    const allowedFocusValues = new Set(availableFocusOptions.map((option) => option.value));

    setSearchFilters((prev) => {
      const nextFocus = prev.focus.filter((value) => allowedFocusValues.has(value));

      if (
        nextFocus.length === prev.focus.length &&
        nextFocus.every((value, index) => value === prev.focus[index])
      ) {
        return prev;
      }

      return {
        ...prev,
        focus: nextFocus,
      };
    });
  }, [availableFocusOptions]);

  useEffect(() => {
    if (!visibleFilters.some((filter) => filter.key === openFilterMenu)) {
      setOpenFilterMenu(null);
    }
  }, [openFilterMenu, visibleFilters]);

  useEffect(() => {
    if (!hasIncompleteSuperset) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasIncompleteSuperset]);

  useEffect(() => {
    if (!workout || !activeSearchTarget) {
      return;
    }

    const targetBlockExists = workout.blocks.some(
      (block) =>
        block.id === activeSearchTarget.blockId &&
        block.type === "superset" &&
        block.exercises[activeSearchTarget.exerciseIndex]
    );

    if (!targetBlockExists) {
      setActiveSearchTarget(null);
    }
  }, [activeSearchTarget, workout]);

  useEffect(() => {
    if (!activeSearchTarget) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (searchUiRef.current?.contains(event.target)) {
        return;
      }

      const targetNode = document.querySelector(
        `[data-superset-block="${activeSearchTarget.blockId}"]`
      );

      if (targetNode?.contains(event.target)) {
        return;
      }

      setActiveSearchTarget(null);
      if (!debouncedSearchQuery) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, { passive: true });

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [activeSearchTarget, debouncedSearchQuery]);

  useEffect(() => {
    if (!shouldShowSearchPanel || activeSearchTarget) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (searchUiRef.current?.contains(event.target)) {
        return;
      }

      setIsSearchOpen(false);
      setOpenFilterMenu(null);
      if (searchQuery.trim()) {
        setSearchQuery("");
        setDebouncedSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, { passive: true });

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [shouldShowSearchPanel, activeSearchTarget, searchQuery]);

  useEffect(() => {
    if (!isWorkoutTitleEditing) {
      return;
    }

    workoutTitleInputRef.current?.focus();
    workoutTitleInputRef.current?.select();
  }, [isWorkoutTitleEditing]);

  useEffect(() => {
    if (!isPastWorkoutOccurrenceLocked) {
      return;
    }

    setIsWorkoutTitleEditing(false);
    setIsSearchOpen(false);
    setOpenFilterMenu(null);
    setActiveSearchTarget(null);
  }, [isPastWorkoutOccurrenceLocked]);

  useEffect(() => {
    if (!pendingScrollToLastBlock || !workout?.blocks.length) {
      return;
    }

    const lastBlockIndex = workout.blocks.length - 1;
    const lastBlock = workout.blocks[workout.blocks.length - 1];
    const lastBlockUiKey = getBlockUiKey(workout.id, lastBlock, lastBlockIndex);
    setOpenBlockId(lastBlockUiKey);

    const frameId = window.requestAnimationFrame(() => {
      document
        .querySelector(`[data-block-id="${lastBlockUiKey}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      setPendingScrollToLastBlock(false);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [pendingScrollToLastBlock, workout]);

  if (!workout) {
    return isRestoringCycle || isRestoringWeeklyPlan ? (
      <main role="status" aria-busy="true" className="lz-workout-editor-loading">
        Loading workout…
      </main>
    ) : null;
  }

  const handleBack = () => {
    const multiWeekBuilderPath = resolveMultiWeekBuilderPath(location.pathname);
    const contextualBackTarget = resolveBackTarget(
      location,
      programDraft.isMultiWeek ? multiWeekBuilderPath : "/program/manual-builder"
    );

    if (location.state?.from) {
      navigate(contextualBackTarget);
      return;
    }

    if (programDraft.isMultiWeek) {
      navigate(multiWeekBuilderPath);
      return;
    }

    navigate("/program/manual-builder");
  };

  // Exactly ONE top-level block -- Single, Superset or Cardio -- may be expanded
  // at a time, and `openBlockId` stays the single source of truth for which one.
  // A Superset is one top-level block that may additionally hold one open lane,
  // so opening a lane makes that Superset the open block rather than adding a
  // second expansion. Leaving a Superset drops its nested lane state, so it
  // reopens with both lanes closed instead of restoring a stale one.
  const toggleBlock = (blockUiKey) => {
    setOpenBlockId((prev) => (prev === blockUiKey ? null : blockUiKey));
    setOpenSupersetExerciseByBlock((prev) =>
      Object.keys(prev).length === 0 ? prev : {}
    );
  };

  // Clearing a lane closes it in the same gesture: an empty lane has no editor,
  // so leaving `openSupersetExerciseByBlock` pointing at it would be a stale
  // open state for an exercise that no longer exists. The data edit itself is
  // the context mutator's, unchanged.
  const removeSupersetLaneExercise = (blockId, exerciseIndex) => {
    clearSupersetLaneExercise(workout.id, blockId, exerciseIndex);
    // The control only exists on the open lane of the open block, and at most
    // one lane is ever open, so dropping the lane state entirely is exactly
    // "close the lane that was just emptied". `openBlockId` is untouched: the
    // superset stays the open top-level block.
    setOpenSupersetExerciseByBlock((prev) =>
      Object.keys(prev).length === 0 ? prev : {}
    );
  };

  const toggleSupersetExercise = (blockUiKey, exerciseIndex) => {
    setOpenBlockId(blockUiKey);
    setOpenSupersetExerciseByBlock((prev) => ({
      [blockUiKey]: prev[blockUiKey] === exerciseIndex ? null : exerciseIndex,
    }));
  };

  const handleBlockDragStart = (event) => {
    const nextActiveSortableId = String(event.active?.id || "").trim();

    if (!nextActiveSortableId) {
      return;
    }

    setActiveSortableId(nextActiveSortableId);
    setOpenBlockId(null);
    setOpenSupersetExerciseByBlock((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, nextActiveSortableId)) {
        return prev;
      }

      const nextState = { ...prev };
      delete nextState[nextActiveSortableId];
      return nextState;
    });
  };

  const handleBlockDragCancel = () => {
    setActiveSortableId(null);
  };

  const handleBlockDragEnd = (event) => {
    const activeId = String(event.active?.id || "").trim();
    const overId = String(event.over?.id || "").trim();

    setActiveSortableId(null);

    if (!activeId || !overId || activeId === overId || !workout) {
      return;
    }

    const fromIndex = (workout.blocks || []).findIndex(
      (block, blockIndex) => getBlockUiKey(workout.id, block, blockIndex) === activeId
    );
    const toIndex = (workout.blocks || []).findIndex(
      (block, blockIndex) => getBlockUiKey(workout.id, block, blockIndex) === overId
    );

    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
      return;
    }

    reorderBlocks(workout.id, fromIndex, toIndex);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveStatusLabel =
    draftMetadata.saveState === "saving"
      ? "Saving..."
      : draftMetadata.saveState === "error"
        ? "Save failed"
        : draftMetadata.lastSavedAt
          ? "Saved"
          : "Draft";

  const handleExerciseResultClick = (exercise) => {
    if (isPastWorkoutOccurrenceLocked) {
      return;
    }

    const selection = toBuilderExerciseSelection(exercise);

    if (activeSearchTarget) {
      assignSupersetExercise(
        workout.id,
        activeSearchTarget.blockId,
        activeSearchTarget.exerciseIndex,
        selection
      );
      setActiveSearchTarget(null);
      setSearchQuery("");
      setDebouncedSearchQuery("");
      setIsSearchOpen(false);
      setOpenFilterMenu(null);
      return;
    }

    appendSingleBlockFromExercise(workout.id, selection);
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setIsSearchOpen(false);
    setOpenFilterMenu(null);
    setPendingScrollToLastBlock(true);
  };

  const activateA2Selection = (blockId, exerciseIndex) => {
    if (isPastWorkoutOccurrenceLocked) {
      return;
    }

    setActiveSearchTarget({
      type: "superset-slot",
      blockId,
      exerciseIndex,
    });
  };

  const handleClearSearch = () => {
    if (isPastWorkoutOccurrenceLocked) {
      return;
    }

    setSearchQuery("");
    setDebouncedSearchQuery("");
  };

  const handleWorkoutTitleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === "Escape") {
      event.preventDefault();
      setIsWorkoutTitleEditing(false);
    }
  };

  const handleFilterValueToggle = (filterKey, value) => {
    if (isPastWorkoutOccurrenceLocked) {
      return;
    }

    setSearchFilters((prev) => {
      const currentValues = prev[filterKey] || [];
      const nextValues = currentValues.includes(value)
        ? currentValues.filter((entry) => entry !== value)
        : [...currentValues, value];

      return {
        ...prev,
        [filterKey]: nextValues,
      };
    });

    setOpenFilterMenu(null);
  };

  const handleFilterClear = (event, filterKey) => {
    event.stopPropagation();
    if (isPastWorkoutOccurrenceLocked) {
      return;
    }

    setSearchFilters((prev) => ({ ...prev, [filterKey]: [] }));
    setOpenFilterMenu(null);
  };

  const getTempoFieldKey = (blockId, exerciseIndex = null) =>
    exerciseIndex == null ? blockId : `${blockId}-${exerciseIndex}`;

  const getRepsFieldKey = (blockId, setIndex, exerciseIndex = null) =>
    exerciseIndex == null
      ? `${blockId}-${setIndex}`
      : `${blockId}-${exerciseIndex}-${setIndex}`;

  const beginTempoEditing = (blockId, currentValue, exerciseIndex = null) => {
    const fieldKey = getTempoFieldKey(blockId, exerciseIndex);
    setTempoDrafts((prev) => ({
      ...prev,
      [fieldKey]: String(currentValue || "").replace(/\D/g, "").slice(0, 4),
    }));
  };

  const changeTempoDraft = (blockId, value, exerciseIndex = null) => {
    const fieldKey = getTempoFieldKey(blockId, exerciseIndex);
    const nextValue = String(value || "")
      .replace(/\D/g, "")
      .split("")
      .filter((digit) => digit >= "0" && digit <= "5")
      .join("")
      .slice(0, 4);

    setTempoDrafts((prev) => ({
      ...prev,
      [fieldKey]: nextValue,
    }));
  };

  const commitTempoDraft = (workoutId, blockId, currentValue, exerciseIndex = null) => {
    const fieldKey = getTempoFieldKey(blockId, exerciseIndex);
    const draftValue = tempoDrafts[fieldKey];
    const normalizedTempo = normalizeTempoValue(
      draftValue == null ? currentValue : draftValue
    );

    if (normalizedTempo !== normalizeTempoValue(currentValue)) {
      if (exerciseIndex == null) {
        updateBlock(workoutId, blockId, { tempo: normalizedTempo });
      } else {
        updateSupersetExercise(workoutId, blockId, exerciseIndex, {
          tempo: normalizedTempo,
        });
      }
    }

    setTempoDrafts((prev) => {
      const nextDrafts = { ...prev };
      delete nextDrafts[fieldKey];
      return nextDrafts;
    });
  };

  // ---------------------------------------------------------------------------
  // D36 rep prescription (Phase 7A)
  //
  // `repsDrafts` is widened -- the single sanctioned local-state extension in
  // this phase -- to carry the range bounds and the timed value alongside the
  // existing scalar reps draft. Ownership does NOT move: the map, its keys and
  // its commit timing all stay in this container. The scalar fixed-reps path is
  // untouched and still writes `{ reps }`, which the Phase 6A mutator already
  // reads as `mode: "reps"`.
  // ---------------------------------------------------------------------------
  const getRepTargetDraftKey = (fieldKey, field) =>
    field === "reps" ? fieldKey : `${fieldKey}::${field}`;

  const getRepDraftsForSet = (blockId, setIndex, exerciseIndex = null) => {
    const fieldKey = getRepsFieldKey(blockId, setIndex, exerciseIndex);
    let drafts;

    REP_DRAFT_FIELDS.forEach((field) => {
      const draftKey = getRepTargetDraftKey(fieldKey, field);

      if (Object.prototype.hasOwnProperty.call(repsDrafts, draftKey)) {
        drafts = drafts || {};
        drafts[field] = repsDrafts[draftKey];
      }
    });

    return drafts;
  };

  const beginRepFieldEditing = (blockId, setIndex, set, slot, exerciseIndex = null) => {
    const prescription = resolveSetRepPrescription(set);
    const fields = resolveStrengthRepFields(prescription);
    const draftKey = getRepTargetDraftKey(
      getRepsFieldKey(blockId, setIndex, exerciseIndex),
      slot
    );
    // The right slot seeds from the persisted MAXIMUM only. A ghosted field
    // seeds empty: its suggestion is a placeholder, never a value.
    const currentValue = slot === "targetSeconds"
      ? prescription.targetSeconds
      : slot === "reps" ? fields.primary : fields.secondary;

    setRepsDrafts((prev) => ({
      ...prev,
      [draftKey]: currentValue == null ? "" : String(currentValue),
    }));
  };

  const changeRepFieldDraft = (blockId, setIndex, slot, value, exerciseIndex = null) => {
    const draftKey = getRepTargetDraftKey(
      getRepsFieldKey(blockId, setIndex, exerciseIndex),
      slot
    );

    setRepsDrafts((prev) => ({
      ...prev,
      [draftKey]: String(value || "").replace(/\D/g, "").slice(0, 3),
    }));
  };

  // The strength rep target is now implicit: the LEFT slot ("reps") is the
  // fixed value or the range minimum, the RIGHT slot ("maxReps") is the range
  // maximum, and the canonical mode follows from which of them ends up filled.
  // Both slots commit through here so one blur always resolves the whole
  // prescription coherently, and every write still leaves as a single atomic
  // Phase 6A `repTarget` edit.
  const commitRepFieldDraft = (blockId, setIndex, set, slot, exerciseIndex = null) => {
    const prescription = resolveSetRepPrescription(set);
    const fields = resolveStrengthRepFields(prescription);
    const fieldKey = getRepsFieldKey(blockId, setIndex, exerciseIndex);
    const primaryKey = getRepTargetDraftKey(fieldKey, "reps");
    const secondaryKey = getRepTargetDraftKey(fieldKey, "maxReps");

    const primaryDraft = repsDrafts[primaryKey];
    const secondaryDraft = repsDrafts[secondaryKey];
    const blurredKey = slot === "reps" ? primaryKey : secondaryKey;

    if (slot === "targetSeconds") {
      const secondsKey = getRepTargetDraftKey(fieldKey, "targetSeconds");
      const secondsDraft = repsDrafts[secondsKey];
      setRepsDrafts((prev) => {
        const nextDrafts = { ...prev };
        delete nextDrafts[secondsKey];
        return nextDrafts;
      });
      if (secondsDraft == null) {
        return;
      }
      const targetSeconds = secondsDraft === ""
        ? null
        : clampRepTargetValue(secondsDraft, MAX_TARGET_SECONDS);
      if (targetSeconds === prescription.targetSeconds) {
        return;
      }
      updateSet(
        workout.id,
        blockId,
        setIndex,
        { repTarget: { mode: REP_MODES.time, targetSeconds } },
        exerciseIndex
      );
      return;
    }

    // Only the slot that blurred releases its draft; the other may still be
    // mid-edit.
    setRepsDrafts((prev) => {
      const nextDrafts = { ...prev };
      delete nextDrafts[blurredKey];
      return nextDrafts;
    });

    if ((slot === "reps" ? primaryDraft : secondaryDraft) == null) {
      return;
    }

    const readSlot = (draft, persisted) => {
      if (draft === undefined) {
        return persisted;
      }

      return draft === "" ? null : clampRepTargetValue(draft, MAX_REP_TARGET);
    };

    const nextPrimary = readSlot(primaryDraft, fields.primary);
    const nextSecondary = readSlot(secondaryDraft, fields.secondary);

    // Focusing and leaving the ghost field changes nothing, so it can never
    // turn a fixed target into a range on its own.
    if (nextPrimary === fields.primary && nextSecondary === fields.secondary) {
      return;
    }

    // Clearing the left field clears the target outright; the old right value
    // is never promoted into a fixed one.
    if (nextPrimary == null) {
      if (fields.primary == null && prescription.mode !== REP_MODES.time) {
        return;
      }

      updateSet(
        workout.id,
        blockId,
        setIndex,
        { repTarget: { mode: REP_MODES.fixed, targetReps: null } },
        exerciseIndex
      );
      return;
    }

    if (nextSecondary == null) {
      updateSet(
        workout.id,
        blockId,
        setIndex,
        { repTarget: { mode: REP_MODES.fixed, targetReps: nextPrimary } },
        exerciseIndex
      );
      return;
    }

    // The domain rejects `minReps > maxReps`. Rather than emit a write the
    // server must refuse, leave the persisted prescription alone -- clearing
    // the draft above has already restored the visible value.
    if (nextPrimary > nextSecondary) {
      return;
    }

    updateSet(
      workout.id,
      blockId,
      setIndex,
      { repTarget: { mode: REP_MODES.range, minReps: nextPrimary, maxReps: nextSecondary } },
      exerciseIndex
    );
  };

  const cycleSetRir = (blockId, setIndex, currentRir, exerciseIndex = null) => {
    const updates = { rpe: getNextRirValue(currentRir) };

    if (exerciseIndex == null) {
      updateSet(workout.id, blockId, setIndex, updates);
      return;
    }

    updateSet(workout.id, blockId, setIndex, updates, exerciseIndex);
  };

  const updateCardioPrescription = (block, updates) => {
    const currentPrescription = getCardioPrescription(block);
    const fullPrescription =
      block.cardioPrescription || block.exercisePersistence?.cardioPrescription || {};
    const hasMachineSettingsUpdate = Object.prototype.hasOwnProperty.call(
      updates,
      "machineSettings"
    );
    const hiddenMachineSettings = Array.isArray(fullPrescription.machineSettings)
      ? fullPrescription.machineSettings.slice(2)
      : [];
    updateBlock(workout.id, block.id, {
      cardioPrescription: {
        ...fullPrescription,
        ...currentPrescription,
        ...updates,
        machineSettings: hasMachineSettingsUpdate
          ? [...(updates.machineSettings || []), ...hiddenMachineSettings]
          : fullPrescription.machineSettings || currentPrescription.machineSettings,
      },
    });
  };

  const updateCardioMachineSetting = (block, settingIndex, updates) => {
    const currentPrescription = getCardioPrescription(block);
    const nextSettings = currentPrescription.machineSettings.map((setting, index) =>
      index === settingIndex ? { ...setting, ...updates } : setting
    );

    updateCardioPrescription(block, { machineSettings: nextSettings });
  };

  const addCardioMachineSetting = (block) => {
    const currentPrescription = getCardioPrescription(block);
    const nextOption = getAvailableCardioMachineSettingOptions(
      block,
      currentPrescription.machineSettings
    )[0];

    if (currentPrescription.machineSettings.length >= 2 || !nextOption) {
      return;
    }

    updateCardioPrescription(block, {
      machineSettings: [
        ...currentPrescription.machineSettings,
        { key: nextOption.value, value: "" },
      ],
    });
  };

  const removeCardioMachineSetting = (block, settingIndex) => {
    const currentPrescription = getCardioPrescription(block);
    updateCardioPrescription(block, {
      machineSettings: currentPrescription.machineSettings.filter(
        (_, index) => index !== settingIndex
      ),
    });
  };

  const updateCardioHeartRateMode = (block, mode) => {
    updateCardioPrescription(block, {
      heartRateTargetMode: mode === "none" ? undefined : mode,
      heartRateTargetValue:
        mode === "avg_bpm" ? 140 : mode === "zone" ? 2 : null,
    });
  };

  const stepCardioBpm = (block, direction) => {
    const currentPrescription = getCardioPrescription(block);
    const nextValue = clampCardioNumber(
      (Number(currentPrescription.heartRateTargetValue) || 140) + direction * 10,
      80,
      200,
      140
    );

    updateCardioPrescription(block, { heartRateTargetValue: nextValue });
  };

  const stepCardioZone = (block, direction) => {
    const currentPrescription = getCardioPrescription(block);
    const nextValue = clampCardioNumber(
      (Number(currentPrescription.heartRateTargetValue) || 2) + direction,
      1,
      5,
      2
    );

    updateCardioPrescription(block, { heartRateTargetValue: nextValue });
  };

  // ---------------------------------------------------------------------------
  // ONE functional props object, shared identically by both renderer branches
  // (blueprint section 7, rules S1-S3). No branch-specific props, no
  // branch-specific handlers, no duplicated persistence.
  // ---------------------------------------------------------------------------
  const rendererProps = {
    workout,
    executionLock: workout?.executionLock || null,
    saveStatusLabel,
    isPastWorkoutOccurrenceLocked,
    isWorkoutTitleEditing,
    workoutTitleInputRef,
    onStartTitleEditing: () => setIsWorkoutTitleEditing(true),
    onStopTitleEditing: () => setIsWorkoutTitleEditing(false),
    onWorkoutNameChange: (value) => updateWorkoutName(workout.id, value),
    onWorkoutTitleKeyDown: handleWorkoutTitleKeyDown,
    onBack: handleBack,
    workoutStatItems,
    visibleMuscleDistribution,
    muscleDistribution: workoutMetrics.muscleDistribution,
    muscleVolume,
    isMuscleDistributionExpanded: showMuscleDistribution,
    onMuscleDistributionExpandedChange: setShowMuscleDistribution,
    maxBlockSetCount: MAX_BLOCK_SET_COUNT,
    showScrollTop,
    onScrollToTop: scrollToTop,
    showAddBlockSheet,
    onCloseAddBlockSheet: () => setShowAddBlockSheet(false),
    addBlockOptions: ADD_BLOCK_OPTIONS,

    // Exercise search: behaviour and state ownership stay exactly where they
    // are. Phase 7B replaces only the presentation behind this seam.
    search: {
      containerRef: searchUiRef,
      searchQuery,
      onSearchQueryChange: setSearchQuery,
      onSearchFocus: () => setIsSearchOpen(true),
      onClearSearch: handleClearSearch,
      disabled: isPastWorkoutOccurrenceLocked,
      shouldShowSearchPanel,
      activeSearchTarget,
      onCancelActiveSearchTarget: () => setActiveSearchTarget(null),
      visibleFilters,
      searchFilters,
      openFilterMenu,
      onToggleFilterMenu: (filterKey) =>
        setOpenFilterMenu((prev) => (prev === filterKey ? null : filterKey)),
      onFilterClear: handleFilterClear,
      onFilterValueToggle: handleFilterValueToggle,
      isLoadingExercises,
      exerciseError,
      exerciseEmptyMessage,
      visibleExerciseResults,
      onExerciseResultClick: handleExerciseResultClick,
    },

    dnd: {
      sensors,
      sortableBlockIds,
      activeSortableId,
      onDragStart: handleBlockDragStart,
      onDragCancel: handleBlockDragCancel,
      onDragEnd: handleBlockDragEnd,
    },

    blocks: {
      openBlockId,
      openSupersetExerciseByBlock,
      onToggleBlock: toggleBlock,
      onToggleSupersetExercise: toggleSupersetExercise,
      onRemoveBlock: (blockId) => removeBlock(workout.id, blockId),
      onDuplicateBlock: (blockId) => duplicateBlock(workout.id, blockId),
      onConvertToSuperset: (blockId) =>
        convertSingleBlockToSuperset(workout.id, blockId),
      onRemoveLaneExercise: (blockId, exerciseIndex) =>
        removeSupersetLaneExercise(blockId, exerciseIndex),
      onBlockNotesChange: (blockId, value) =>
        updateBlock(workout.id, blockId, { notes: value }),
      onLaneNotesChange: (blockId, exerciseIndex, value) =>
        updateSupersetExercise(workout.id, blockId, exerciseIndex, { notes: value }),
      onRestStep: (blockId, currentRest, direction) =>
        updateBlock(workout.id, blockId, {
          rest: getNextRestValue(currentRest, direction),
        }),
      onRoundsChange: (blockId, nextCount) =>
        updateSupersetSetCount(workout.id, blockId, nextCount),
      onAddSet: (blockId) => addSet(workout.id, blockId),
      onRemoveSet: (blockId, setIndex) => removeSet(workout.id, blockId, setIndex),
      onCycleRir: cycleSetRir,
      onSelectLaneExercise: activateA2Selection,

      tempoValueFor: (blockId, currentTempo, exerciseIndex = null) =>
        tempoDrafts[getTempoFieldKey(blockId, exerciseIndex)] ??
        formatTempoValue(currentTempo),
      onTempoFocus: (blockId, currentTempo, exerciseIndex = null) =>
        beginTempoEditing(blockId, currentTempo, exerciseIndex),
      onTempoChange: (blockId, value, exerciseIndex = null) =>
        changeTempoDraft(blockId, value, exerciseIndex),
      onTempoBlur: (blockId, currentTempo, exerciseIndex = null) =>
        commitTempoDraft(workout.id, blockId, currentTempo, exerciseIndex),

      prescriptionForSet: resolveSetRepPrescription,
      repDraftsFor: getRepDraftsForSet,
      onRepFieldFocus: beginRepFieldEditing,
      onRepFieldChange: changeRepFieldDraft,
      onRepFieldBlur: commitRepFieldDraft,

      cardioPrescriptionFor: getCardioPrescription,
      cardioExerciseNameFor: getCardioBlockExerciseName,
      cardioHeartRateModeOptions: CARDIO_HEART_RATE_MODE_OPTIONS,
      cardioZoneValueFor: (prescription) =>
        clampCardioNumber(prescription.heartRateTargetValue, 1, 5, 2),
      machineSettingOptionsFor: getCardioMachineSettingOptions,
      machineSettingLabelFor: getCardioMachineSettingLabel,
      usedMachineSettingKeysFor: getUsedCardioMachineSettingKeys,
      availableMachineSettingOptionsFor: getAvailableCardioMachineSettingOptions,
      onCardioDurationChange: (block, value) =>
        updateCardioPrescription(block, {
          durationMinutes: value === "" ? "" : Number(value),
        }),
      onCardioHeartRateModeChange: updateCardioHeartRateMode,
      onCardioHeartRateValueChange: (block, value) =>
        updateCardioPrescription(block, {
          heartRateTargetValue:
            value === "" ? null : clampCardioNumber(Number(value), 80, 200, 140),
        }),
      onStepCardioHeartRate: (block, heartRateMode, direction) =>
        heartRateMode === "zone"
          ? stepCardioZone(block, direction)
          : stepCardioBpm(block, direction),
      onAddMachineSetting: addCardioMachineSetting,
      onUpdateMachineSetting: updateCardioMachineSetting,
      onRemoveMachineSetting: removeCardioMachineSetting,
      onCardioNotesChange: (block, value) =>
        updateCardioPrescription(block, { notes: value }),
    },
  };

  return <WorkoutBuilderRendererV2 {...rendererProps} />;
}
